import { getLocalTimeZone, parseAbsoluteToLocal } from "@norican/react";
import { type ComponentPropsWithoutRef, useEffect, useMemo, useState } from "react";

import { useLanguage, useT } from "@/i18n";
import { logger } from "@/lib/logger";

/** Anything the API might hand us for a timestamp. */
export type DateInput = string | number | Date | null | undefined;

/**
 * Parses an API timestamp.
 *
 * The contract says ISO-8601 UTC with `Z`, so the design system's
 * `parseAbsoluteToLocal` (re-exported `@internationalized/date`) is the primary
 * path: it resolves the instant against the viewer's zone rather than the
 * browser's inconsistent `Date` parsing. Anything it rejects falls back to
 * `new Date`, and anything that fails both is treated as "no date" rather than
 * rendering `Invalid Date` to a user.
 */
export function parseDateInput(value: DateInput): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  try {
    return parseAbsoluteToLocal(value).toDate();
  } catch {
    const fallback = new Date(value);
    if (Number.isNaN(fallback.getTime())) {
      logger.debug("[relative-time] unparseable timestamp", value);
      return null;
    }
    return fallback;
  }
}

const RELATIVE_DIVISIONS: readonly { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>();
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

function getRelativeFormatter(locale: string): Intl.RelativeTimeFormat {
  const cached = relativeFormatters.get(locale);
  if (cached) {
    return cached;
  }
  const created = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  relativeFormatters.set(locale, created);
  return created;
}

function getDateTimeFormatter(locale: string, withTime: boolean): Intl.DateTimeFormat {
  const key = `${locale}:${withTime ? "datetime" : "date"}`;
  const cached = dateTimeFormatters.get(key);
  if (cached) {
    return cached;
  }
  const created = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    ...(withTime ? { timeStyle: "short" } : {}),
    timeZone: getLocalTimeZone(),
  });
  dateTimeFormatters.set(key, created);
  return created;
}

/** "3 minutes ago" / "in 2 days", in the given locale. */
export function formatRelative(target: Date, locale: string, now: Date = new Date()): string {
  const formatter = getRelativeFormatter(locale);
  let duration = (target.getTime() - now.getTime()) / 1000;
  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), "year");
}

/**
 * Locale-aware date/time formatters bound to the active language.
 *
 * Use this when you need a formatted string rather than an element — a
 * `DataGrid` column accessor, a `Timeline` item `timestamp`, an `aria-label`.
 */
export function useDateFormatter() {
  const { locale } = useLanguage();
  const { t } = useT();

  return useMemo(
    () => ({
      locale,
      /** "14 Mar 2026" */
      formatDate: (value: DateInput): string => {
        const parsed = parseDateInput(value);
        return parsed === null
          ? t("time.unknown")
          : getDateTimeFormatter(locale, false).format(parsed);
      },
      /** "14 Mar 2026, 09:12" */
      formatDateTime: (value: DateInput): string => {
        const parsed = parseDateInput(value);
        return parsed === null
          ? t("time.unknown")
          : getDateTimeFormatter(locale, true).format(parsed);
      },
      /** "3 minutes ago" */
      formatRelative: (value: DateInput): string => {
        const parsed = parseDateInput(value);
        return parsed === null ? t("time.unknown") : formatRelative(parsed, locale);
      },
    }),
    [locale, t],
  );
}

/** Under a minute old, relative time reads oddly; say "just now" instead. */
const JUST_NOW_MS = 45 * 1000;
/** Past this age, an absolute date is more useful than "4 months ago". */
const RELATIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** How often a visible relative timestamp re-renders so it does not go stale. */
const TICK_MS = 60 * 1000;

export interface RelativeTimeProps extends Omit<
  ComponentPropsWithoutRef<"time">,
  "dateTime" | "children"
> {
  /** The instant to render. ISO-8601 from the API, or a `Date`. */
  value: DateInput;
  /**
   * `auto` (default) shows relative time for the last week and an absolute date
   * beyond it; `relative` and `absolute` force one or the other.
   */
  mode?: "auto" | "relative" | "absolute";
  /** Include the clock time in the absolute rendering. Defaults to `true`. */
  withTime?: boolean;
  /** Text rendered when `value` is null or unparseable. Defaults to "Date unknown". */
  fallback?: string;
}

/**
 * A timestamp rendered in the active language.
 *
 * Emits a real `<time datetime="…">` so the machine-readable instant survives
 * translation, and puts the full absolute date in `title` so the precise value
 * is one hover away when the visible text is relative. Relative renderings
 * refresh once a minute — "just now" that is quietly forty minutes old is worse
 * than no timestamp at all.
 */
export function RelativeTime({
  value,
  mode = "auto",
  withTime = true,
  fallback,
  ...props
}: RelativeTimeProps) {
  const { locale } = useLanguage();
  const { t } = useT();
  const parsed = parseDateInput(value);

  const isRelative =
    parsed !== null &&
    (mode === "relative" ||
      (mode === "auto" && Math.abs(Date.now() - parsed.getTime()) < RELATIVE_WINDOW_MS));

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRelative) {
      return undefined;
    }
    const timer = window.setInterval(() => setTick((previous) => previous + 1), TICK_MS);
    return () => window.clearInterval(timer);
  }, [isRelative]);

  if (parsed === null) {
    return <span {...props}>{fallback ?? t("time.unknown")}</span>;
  }

  const absolute = getDateTimeFormatter(locale, withTime).format(parsed);
  const elapsed = Math.abs(Date.now() - parsed.getTime());
  const display = isRelative
    ? elapsed < JUST_NOW_MS
      ? t("time.justNow")
      : formatRelative(parsed, locale)
    : absolute;

  return (
    <time dateTime={parsed.toISOString()} title={absolute} {...props}>
      {display}
    </time>
  );
}
